import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';

// Component & Asset Imports
import Toptext from '../../components/Toptext';
import Globe from '../../assets/icons/globe.svg';
import Button1 from '../../components/ui/Button1';
import Button2 from '../../components/ui/Button2';
import DynamicHeading from '../../components/ui/CustomHeading';
import { FaDownload, FaRocket, FaShieldAlt, FaUsers, FaCog, FaGlobe, FaChartLine, FaAward, FaIndustry, FaCode } from 'react-icons/fa';
import InvestBgImage from "../../assets/images/invest-bg.png";
import "./styles.css";

gsap.registerPlugin(ScrollTrigger);

const InvestPage = () => {
    const mainRef = useRef(null);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        investmentInterest: '',
        budgetBracket: '',
        message: ''
    });

    const handleInputChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        console.log('Form submitted:', formData);
    };

    const investmentInterests = [
        'Strategic Partnership',
        'Angel Investment',
        'Venture Capital',
        'Joint Venture / Tech Licensing'
    ];

    const budgetBrackets = [
        'Under $100K',
        '$100K - $500K',
        '$500K - $1M',
        '$1M - $5M',
        '$5M+'
    ];

    useEffect(() => {
        document.title = 'Invest | JBS Capacitors';
        window.scrollTo(0, 0);
    }, []);

    useGSAP(() => {
        // Hero Section Intro
        const tl = gsap.timeline({ defaults: { ease: 'power3.out', duration: 0.8 } });
        tl.from('.anim-toptext', { y: -30, opacity: 0 })
          .from('.anim-main-heading span', { y: 40, opacity: 0, stagger: 0.1 }, "-=0.5")
          .from('.anim-subtext', { y: 20, opacity: 0 }, "-=0.6")
          .from('.anim-button', { scale: 0.8, opacity: 0 }, "-=0.5");

        // Animate sections on scroll
        const animateOnScroll = (selector) => {
            gsap.from(selector, {
                y: 50,
                opacity: 0,
                duration: 1,
                ease: 'power3.out',
                scrollTrigger: {
                    trigger: selector,
                    start: 'top 85%',
                    toggleActions: 'play none none none'
                }
            });
        };

        ['.anim-why-partner', '.anim-our-edge', '.anim-downloadable', '.anim-trading-tech', '.anim-investor-contact', '.anim-closing-banner'].forEach(sel => {
            const el = document.querySelector(sel);
            if (el) animateOnScroll(el);
        });
    }, { scope: mainRef });

    return (
        <div ref={mainRef} className='mt-12 max-w-[100vw] mx-auto flex flex-col items-center justify-center overflow-x-hidden'>
            {/* Hero Section */}
            <div className='hero-section relative w-full min-h-[750px] flex items-center justify-center overflow-hidden'>
                <div className='absolute inset-0 bg-gradient-to-br from-jbsBlue/10 to-blue-900/90 z-10'>
                    <img
                        src={InvestBgImage}
                        alt="InvestBgImage"
                        className="w-full blur-[2px] h-full object-cover object-[90%_center]"
                    />
                </div>
                <div className='absolute inset-0 bg-black/50 z-20'></div>
                <div className='relative z-30 text-center text-white px-4 max-w-6xl flex-1'>
                    <div className="anim-toptext">
                        <Toptext icon={Globe} variant="white">
                            Partner with JBS Robotics — The Future of AI-Enabled Automation
                        </Toptext>
                    </div>
                    <div className='mt-12'>
                        <div className="anim-main-heading">
                            <DynamicHeading
                                parts={[
                                    { text: 'Join us as we scale' },
                                    { text: 'globally', highlight: true },
                                    { lineBreak: true },
                                    { text: 'with disruptive farm robots' },
                                    { lineBreak: true },
                                    { text: 'and next-gen trading platforms', highlight: true },
                                ]}
                                variant="white"
                                className="text-white"
                            />
                        </div>
                        <p className='anim-subtext font-light text-white/90 mt-6 text-lg md:text-xl max-w-4xl mx-auto'>
                            We're building the future of robotics, agriculture, and AI.
                            Join our mission to revolutionize automation and create sustainable solutions for tomorrow's challenges.
                        </p>
                        <div className='mt-10 anim-button'>
                            <Button1 width={"300px"}>
                                Download Investor Decks
                            </Button1>
                        </div>
                    </div>
                </div>
            </div>

            {/* Why Partner With Us Section */}
            <div className='w-full bg-white py-20 px-4 anim-why-partner'>
                <div className='max-w-7xl mx-auto'>
                    <div className='text-center mb-16'>
                        <DynamicHeading
                            parts={[
                                { text: 'Why Partner' },
                                { text: 'With Us?', highlight: true },
                            ]}
                        />
                        <p className='text-gray-600 mt-4 text-lg max-w-3xl mx-auto'>
                            We're not just another startup. We're a proven company with decades of experience,
                            institutional backing, and a clear path to global scale.
                        </p>
                    </div>
                    <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8'>
                        {[
                            { icon: <FaRocket color='white'/>, title: 'Disrupting Agri-Automation', description: 'Leading the revolution in agricultural robotics with proprietary technology and competitive pricing.' },
                            { icon: <FaShieldAlt color='white'/>, title: 'Institutional Backing', description: 'Not just a startup — we have strong institutional support and proven track record.' },
                            { icon: <FaUsers color='white'/>, title: 'Experienced AI Team', description: 'Led by veterans in AI with multiple patents and global academic publications.' },
                            { icon: <FaCog color='white'/>, title: 'Full-Stack Capabilities', description: 'Electronics + AI + Software + Manufacturing under one roof.' },
                            { icon: <FaGlobe color='white'/>, title: 'Global Reach', description: 'UK-India presence with strong institutional base and cross-border operations.' },
                            { icon: <FaAward color='white'/>, title: 'Proven Products', description: 'AgTron Rover, Agrotronic Farms, and Trading Platform suite already in market.' }
                        ].map((item, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 50 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                viewport={{ once: true }}
                                className='bg-gradient-to-br from-blue-50 to-jbsBlue/10 p-8 rounded-[38px] border border-blue-100 hover:shadow-lg transition-all duration-300'
                            >
                                <div className='w-16 h-16 bg-jbsBlue rounded-full mx-auto mb-6 flex items-center justify-center'>
                                    {item.icon}
                                </div>
                                <h3 className='text-xl font-bold text-jbsGray mb-3 text-center'>{item.title}</h3>
                                <p className='text-gray-600 text-center'>{item.description}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Our Edge Over Startups Section */}
            <div className='w-full bg-gray-50 py-20 px-4 anim-our-edge'>
                <div className='max-w-7xl mx-auto'>
                    <div className='text-center mb-16'>
                        <DynamicHeading
                            parts={[
                                { text: 'Our Edge Over' },
                                { text: 'Startups', highlight: true },
                            ]}
                        />
                    </div>
                    <div className='grid grid-cols-1 lg:grid-cols-2 gap-12'>
                        <div className='space-y-8'>
                            {[
                                { icon: <FaUsers color='white'/>, title: 'Veteran Team', description: 'Led by veterans in AI with multiple patents, global academic publications, and experience in self-driving cars.' },
                                { icon: <FaIndustry color='white'/>, title: 'Legacy & Credibility', description: 'Not a new startup — decades-old group pivoting to robotics since the 2010s with established credibility.' },
                                { icon: <FaCode color='white'/>, title: 'In-House Capabilities', description: 'Electronics design, software, embedded AI, and field deployment all under one roof.' }
                            ].map((item, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, x: -50 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.5, delay: index * 0.1 }}
                                    viewport={{ once: true }}
                                    className='flex items-start space-x-4'
                                >
                                    <div className='w-12 h-12 bg-jbsBlue rounded-full flex items-center justify-center flex-shrink-0'>
                                        {item.icon}
                                    </div>
                                    <div>
                                        <h3 className='text-xl font-bold text-jbsGray mb-2'>{item.title}</h3>
                                        <p className='text-gray-600'>{item.description}</p>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.5 }}
                            viewport={{ once: true }}
                            className='bg-white p-8 rounded-[38px] shadow-lg'
                        >
                            <h3 className='text-2xl font-bold text-jbsGray mb-6'>Investment Highlights</h3>
                            <div className='space-y-4'>
                                {[
                                    'Proven market traction with existing customers',
                                    'Multiple revenue streams across robotics and trading',
                                    'Strong IP portfolio with patents and proprietary tech',
                                    'Global expansion opportunities in UK and India',
                                    'Scalable business model with recurring revenue'
                                ].map((highlight, index) => (
                                    <div key={index} className='flex items-center space-x-3'>
                                        <div className='w-3 h-3 bg-jbsBlue rounded-full'></div>
                                        <span className='text-gray-700'>{highlight}</span>
                                    </div>
                                ))}
                            </div>
                        </motion.div>
                    </div>
                </div>
            </div>

            {/* Downloadable Pitch Decks Section */}
            <div className='w-full bg-white py-20 px-4 anim-downloadable'>
                <div className='max-w-6xl mx-auto'>
                    <div className='text-center mb-16'>
                        <DynamicHeading
                            parts={[
                            { text: 'Investor Resources &' },
                                { text: 'Decks', highlight: true },
                            ]}
                        />
                        <p className='text-gray-600 mt-4 text-lg'>
                            Download our comprehensive pitch decks to learn more about our products and vision.
                        </p>
                    </div>
                    <div className='grid grid-cols-1 md:grid-cols-3 gap-8'>
                        {[
                            { icon: <FaDownload color='white'/>, title: 'AgTron Rover Pitch Deck', description: 'Comprehensive overview of our flagship agricultural robotics platform.' },
                            { icon: <FaDownload color='white'/>, title: 'Agrotronic Farms Overview', description: 'Smart farming solutions and sustainable agriculture technology.' },
                            { icon: <FaDownload color='white'/>, title: 'Greenhouse Automation', description: 'Advanced automation solutions for controlled environment agriculture.' }
                        ].map((deck, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 50 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                viewport={{ once: true }}
                                className='bg-gradient-to-br from-blue-50 to-jbsBlue/10 p-8 rounded-[38px] border border-blue-100 text-center hover:shadow-lg transition-all duration-300'
                            >
                                <div className='w-20 h-20 bg-jbsBlue rounded-full mx-auto mb-6 flex items-center justify-center'>
                                    {deck.icon}
                                </div>
                                <h3 className='text-xl font-bold text-jbsGray mb-3'>{deck.title}</h3>
                                <p className='text-gray-600 mb-6'>{deck.description}</p>
                                <Button1 width={"180px"}>Download PDF</Button1>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Trading Tech Opportunity Section */}
            <div className='w-full bg-gradient-to-r from-jbsBlue to-blue-600 py-16 px-4 anim-trading-tech'>
                <div className='max-w-4xl mx-auto text-center text-white'>
                    <div className='w-16 h-16 bg-white/20 rounded-full mx-auto mb-6 flex items-center justify-center'>
                        <FaChartLine className='text-white text-2xl' />
                    </div>
                    <h2 className='text-3xl font-bold mb-6'>What About Our Trading Platforms?</h2>
                    <p className='text-lg mb-8 opacity-90 leading-relaxed'>
                        Our next-gen charting and AI-integrated algo-trading platforms aim to redefine how traders operate.
                        With backends like cTrader and OANDA for traditional instruments and a unique Solana-based token
                        trading interface, we are building the next "TradingView" for the AI age.
                    </p>
                    <Button2 width={"250px"}>
                        <a href="/products&services/Trading-Platforms" className='text-white'>
                            See Trading Platform Demos
                        </a>
                    </Button2>
                </div>
            </div>

            {/* Investor Contact Form */}
            <div className='w-full bg-white py-20 px-4 anim-investor-contact'>
                <div className='max-w-4xl mx-auto'>
                    <div className='text-center mb-12'>
                        <DynamicHeading
                            parts={[
                                { text: 'Connect With' },
                                { text: 'Us', highlight: true },
                            ]}
                        />
                        <p className='text-gray-600 mt-4 text-lg'>
                            Ready to partner with us? Let's discuss how we can work together to scale globally.
                        </p>
                    </div>
                    <div className='bg-gradient-to-br from-gray-50 to-blue-50 p-8 rounded-[38px] border border-gray-200'>
                        <form onSubmit={handleSubmit} className='space-y-6'>
                            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                                <div>
                                    <label htmlFor="name" className='block text-sm font-semibold text-jbsGray mb-2'>
                                        Full Name *
                                    </label>
                                    <input
                                        type="text"
                                        id="name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        required
                                        className='w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-jbsBlue focus:border-transparent transition-all duration-200'
                                        placeholder="Enter your full name"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="email" className='block text-sm font-semibold text-jbsGray mb-2'>
                                        Email Address *
                                    </label>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        required
                                        className='w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-jbsBlue focus:border-transparent transition-all duration-200'
                                        placeholder="Enter your email address"
                                    />
                                </div>
                            </div>
                            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                                <div>
                                    <label htmlFor="investmentInterest" className='block text-sm font-semibold text-jbsGray mb-2'>
                                        Investment Interest *
                                    </label>
                                    <select
                                        id="investmentInterest"
                                        name="investmentInterest"
                                        value={formData.investmentInterest}
                                        onChange={handleInputChange}
                                        required
                                        className='w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-jbsBlue focus:border-transparent transition-all duration-200'
                                    >
                                        <option value="">Select your interest</option>
                                        {investmentInterests.map((interest, index) => (
                                            <option key={index} value={interest}>{interest}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label htmlFor="budgetBracket" className='block text-sm font-semibold text-jbsGray mb-2'>
                                        Budget Bracket
                                    </label>
                                    <select
                                        id="budgetBracket"
                                        name="budgetBracket"
                                        value={formData.budgetBracket}
                                        onChange={handleInputChange}
                                        className='w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-jbsBlue focus:border-transparent transition-all duration-200'
                                    >
                                        <option value="">Select budget range</option>
                                        {budgetBrackets.map((bracket, index) => (
                                            <option key={index} value={bracket}>{bracket}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label htmlFor="message" className='block text-sm font-semibold text-jbsGray mb-2'>
                                    Message *
                                </label>
                                <textarea
                                    id="message"
                                    name="message"
                                    value={formData.message}
                                    onChange={handleInputChange}
                                    required
                                    rows="6"
                                    className='w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-jbsBlue focus:border-transparent transition-all duration-200 resize-none'
                                    placeholder="Tell us about your investment goals and how we can work together..."
                                ></textarea>
                            </div>
                            <div className='flex justify-center pt-4'>
                                <Button1 width={"200px"} type="submit">
                                    Connect With Us
                                </Button1>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            {/* Closing Banner */}
            <div className='w-full bg-gradient-to-r from-jbsBlue to-blue-600 py-16 px-4 mb-20 anim-closing-banner'>
                <div className='max-w-4xl mx-auto text-center text-white'>
                    <h2 className='text-3xl font-bold mb-6'>Help us scale globally</h2>
                    <p className='text-xl mb-8 opacity-90'>
                        Join us in reshaping the future of robotics, agriculture, and AI.
                    </p>
                    <div className='flex flex-col sm:flex-row gap-4 justify-center'>
                        <Button2 width={"200px"}>
                            <a href="mailto:invest@jbsrobotics.com" className='text-white'>
                                Let's Talk
                            </a>
                        </Button2>
                        <Button2 width={"200px"}>
                            <a href="/contact" className='text-white'>
                                Schedule Call
                            </a>
                        </Button2>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InvestPage;
